<template>
  <div
    :class="{
      contentContainer: !inLimit,
      'flex-column': inLimit || inCustomerLimit,
      grow: inLimit || inCustomerLimit,
      h100: inLimit || inCustomerLimit,
      'justify-content-space-between': inCustomerLimit,
    }"
  >
    <div
      v-if="route.name === 'financier'"
      class="flex-column align-items-center padding0 gap24 stretch"
    >
      <h2 class="h bold text-center">
        {{ label }}
      </h2>
    </div>
    <div
      :class="{
        'whitebox-shadowed': inFlow,
        whitebox: inCustomerLimit,
        grow: inLimit,
      }"
      class="flex-column align-items-center padding16 gap24 stretch"
    >
      <template v-if="emailConfirm.emailConfirmStatus === 0">
        <div>
          <IllustrationsEmail />
        </div>
        <div class="buttonContainer stretch">
          <div class="inputField">
            <label class="textBold" for="mail">{{
              $t("bnpl:emailAddress")
            }}</label>
            <input
              id="mail"
              v-model="emailConfirm.customerEmail"
              type="email"
              class="textInput"
              :placeholder="$t('bnpl:email_input_placeholder')"
            />
          </div>
          <button
            id="bnpl_email_submit_button"
            class="btn btnL grow"
            :disabled="
              !emailConfirm.customerEmail ||
              !validateEmail(emailConfirm.customerEmail)
            "
            @click="submitEmail"
          >
            {{ $t("bnpl:confirm") }}
          </button>
        </div>
        <p
          v-if="emailConfirm.showConflictMessage"
          class="p-text bold stretch text-center"
          style="color: var(--systemError)"
        >
          {{ $t("bnpl:emailAlreadyUsed") }}
        </p>
        <p
          v-if="
            emailConfirm.customerEmail &&
            !validateEmail(emailConfirm.customerEmail)
          "
          class="p-caption bold"
          style="color: var(--systemError)"
        >
          {{ $t("bnpl:email_confirmation.invalid_email") }}
        </p>
      </template>

      <template v-else-if="emailConfirm.emailConfirmStatus > 0">
        <template v-if="emailConfirm.emailConfirmStatus === 1">
          <div
            class="flex-column justify-content-center align-items-start padding0 gap32 stretch"
          >
            <div
              class="flex-row justify-content-center align-items-center padding0 gap24"
            >
              <div style="width: 80px; height: 80px">
                <IllustrationsShield />
              </div>
              <h2 class="h bold">
                {{ $t("bnpl:checkCode") }}
              </h2>
            </div>
          </div>
          <div>
            <p class="p-body">
              {{ $t("bnpl:askForCheckCode") }}
            </p>
          </div>
        </template>

        <template v-else-if="emailConfirm.emailConfirmStatus === 2">
          <div
            class="flex-column justify-content-center align-items-center padding0 gap32"
          >
            <div class="flex-row align-items-center padding0 gap16">
              <div style="width: 39px; height: 39px">
                <IllustrationsErrorCross />
              </div>
              <div class="tryAgain">
                {{ $t("bnpl:emailConfirm.fail") }}
              </div>
            </div>
            <p class="p-body">
              {{ $t("bnpl:tryAgain") }}
            </p>
          </div>
        </template>

        <template v-else-if="emailConfirm.emailConfirmStatus === 3">
          <div
            class="flex-column justify-content-center align-items-center padding0 gap32"
          >
            <div
              v-if="inFlow"
              class="flex-row align-items-center padding0 gap16"
            >
              <div style="width: 39px; height: 39px">
                <IllustrationsSuccessTick />
              </div>
              <div class="successful">
                {{ $t("bnpl:emailConfirm.success") }}
              </div>
            </div>
            <template v-if="!inFlow">
              <IllustrationsSuccess />
              <div class="successful stretch text-center">
                {{ $t("bnpl:emailChange.success") }}
              </div>
              <button
                v-if="!(inLimit || inCustomerLimit)"
                id="bnpl_email_close_button"
                class="btn btnL btnGray stretch"
                @click="$emit('close')"
              >
                {{ $t("bnpl:close") }}
              </button>
            </template>
          </div>
        </template>

        <div
          v-if="inFlow || emailConfirm.emailConfirmStatus < 3"
          id="bnpl_email_check_code_inputs"
          class="flex-row align-items-center padding0 checkCodeDigitsContainer stretch"
        >
          <input
            id="d1"
            :ref="inputRefs.d1"
            v-model="emailConfirm.checkCodeDigits[0]"
            class="textInput checkCodeInputField"
            maxlength="1"
            :min="0"
            :max="9"
            :disabled="emailConfirm.emailConfirmStatus === 3"
            type="number"
            autocomplete="one-time-code"
            autofocus
            @keydown="focusFromTo($event, 0, inputRefs.d2, inputRefs.d1)"
          />
          <input
            id="d2"
            :ref="inputRefs.d2"
            v-model="emailConfirm.checkCodeDigits[1]"
            class="textInput checkCodeInputField"
            maxlength="1"
            :min="0"
            :max="9"
            :disabled="emailConfirm.emailConfirmStatus === 3"
            type="number"
            @keydown="focusFromTo($event, 1, inputRefs.d3, inputRefs.d1)"
          />
          <input
            id="d3"
            :ref="inputRefs.d3"
            v-model="emailConfirm.checkCodeDigits[2]"
            class="textInput checkCodeInputField"
            maxlength="1"
            :min="0"
            :max="9"
            :disabled="emailConfirm.emailConfirmStatus === 3"
            type="number"
            @keydown="focusFromTo($event, 2, inputRefs.d4, inputRefs.d2)"
          />
          <input
            id="d4"
            :ref="inputRefs.d4"
            v-model="emailConfirm.checkCodeDigits[3]"
            class="textInput checkCodeInputField"
            maxlength="1"
            :min="0"
            :max="9"
            :disabled="emailConfirm.emailConfirmStatus === 3"
            type="number"
            @keydown="focusFromTo($event, 3, inputRefs.d5, inputRefs.d3)"
          />
          <input
            id="d5"
            :ref="inputRefs.d5"
            v-model="emailConfirm.checkCodeDigits[4]"
            class="textInput checkCodeInputField"
            maxlength="1"
            :min="0"
            :max="9"
            :disabled="emailConfirm.emailConfirmStatus === 3"
            type="number"
            @keydown="focusFromTo($event, 4, inputRefs.d6, inputRefs.d4)"
          />
          <input
            id="d6"
            :ref="inputRefs.d6"
            v-model="emailConfirm.checkCodeDigits[5]"
            class="textInput checkCodeInputField"
            maxlength="1"
            :min="0"
            :max="9"
            :disabled="emailConfirm.emailConfirmStatus === 3"
            type="number"
            @keydown="
              focusFromTo($event, 5, submitCheckCodeButton, inputRefs.d5)
            "
          />
        </div>
        <div
          v-if="inFlow || emailConfirm.emailConfirmStatus < 3"
          class="buttonContainer stretch"
          style="align-items: center"
        >
          <button
            id="bnpl_email_resend_check_code_button"
            class="linkButton linkButtonSuccess"
            :disabled="
              emailConfirm.emailConfirmStatus > 2 || emailConfirm.seconds > 0
            "
            @click="resendCheckCode"
          >
            <i class="pi pi-refresh" />{{ $t("bnpl:resendCode") }}
          </button>
          <button
            id="submitCode"
            ref="submitCheckCodeButton"
            :disabled="emailConfirm.emailConfirmStatus > 2"
            class="btn btnL grow"
            @click="submitCheckCode"
          >
            {{ $t("bnpl:confirm") }}
          </button>
        </div>
        <div
          v-if="emailConfirm.seconds > 0 && emailConfirm.emailConfirmStatus < 3"
          class="stretch text-center"
        >
          <p class="p-body bold" style="color: var(--systemWarning)">
            <strong>{{ emailConfirm.seconds }}</strong>
            {{ $t("bnpl:canSendNewCodeAfterSec") }}
          </p>
        </div>
      </template>
    </div>
    <div
      v-if="
        route.name === 'financier-publicId' ||
        route.name === 'financier-prescore-prescoreId'
      "
      class="flex-row align-items-start padding0 gap24 stretch"
    >
      <div class="flex-row stretch grow">
        <button
          v-if="
            emailConfirm.emailConfirmStatus === 1 ||
            emailConfirm.emailConfirmStatus === 2
          "
          id="bnpl_email_to_start_status_button"
          class="linkButton linkButtonSuccess"
          @click="toStartStatus"
        >
          <i class="pi pi-arrow-left" />{{ $t("bnpl:previousPage") }}
        </button>
      </div>
      <button
        v-if="
          route.name === 'financier-publicId' ||
          route.name === 'financier-prescore-prescoreId'
        "
        id="bnpl_email_continue_button"
        :disabled="emailConfirm.emailConfirmStatus < 3"
        class="btn btnL grow"
        @click="stepToFlow"
      >
        {{ $t("bnpl:continue") }}
      </button>
    </div>
    <div v-if="inLimit" class="actionArea">
      <button
        class="btn btnL rightAction"
        :disabled="emailConfirm.emailConfirmStatus < 3"
        @click="nextLimitStep"
      >
        {{ $t("bnpl:continue") }}
      </button>
    </div>
    <div v-if="inCustomerLimit" class="actionArea">
      <button
        class="btn btnL rightAction"
        :disabled="emailConfirm.emailConfirmStatus < 3"
        @click="nextCustomerLimitStep"
      >
        {{ $t("bnpl:continue") }}
      </button>
    </div>
  </div>
</template>

<script setup>
// Statuses:
// email field: 0
// check code field: 1
// success: 3
// fail: 2

import { useRoute } from "vue-router";
import { useBnplApplicationStore } from "~/stores/bnplapplication";
import { useProfileStore } from "~/stores/profile";
import { useUserStore } from "~/stores/user";
import { useSessionStore } from "~~/stores/session";
import { useLimitStore } from "~~/stores/limit";
import { useChatStore } from "~~/stores/chat";
import { useCustomerLimitStore } from "~~/stores/customerLimit";
import { useCountryStore } from "~~/stores/country";

defineEmits(["close"]);

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: "E-mail cím megerősítése",
  },
  description: {
    type: String,
    required: false,
    default: "Az e-mail cím megerősítéséhez szükséged lesz az e-mail fiókodra!",
  },
  inFlow: {
    type: Boolean,
    required: false,
    default: false,
  },
  inLimit: {
    type: Boolean,
    required: false,
    default: false,
  },
  inCustomerLimit: {
    type: Boolean,
    required: false,
    default: false,
  },
});

onMounted(() => {
  if (
    [
      "financier-publicId",
      "financier-prescore",
      "financier-prescore-prescoreId",
      "financier-limit",
    ].includes(route.name) &&
    profileStore.profile &&
    profileStore.profile.emailConfirmed
  ) {
    emailConfirm.emailConfirmStatus = 3;
  }
});

onBeforeMount(() => {
  chatStore.specialFieldFlowPosition = "FLOW_EMAIL";
});

onBeforeUnmount(() => {
  chatStore.specialFieldFlowPosition = null;
  stopPolling();
});

const bnplApplicationStore = useBnplApplicationStore();
const userStore = useUserStore();
const profileStore = useProfileStore();
const sessionStore = useSessionStore();
const limitStore = useLimitStore();
const customerLimitStore = useCustomerLimitStore();
const chatStore = useChatStore();
const countryStore = useCountryStore();

const nonce = ref(null);
const route = useRoute();

const submitCheckCodeButton = ref(null);

const isCheckCodeSubmitted = ref(false);

const inputRefs = {
  d1: ref(),
  d2: ref(),
  d3: ref(),
  d4: ref(),
  d5: ref(),
  d6: ref(),
};

let secondFactorId = null;

const emailConfirm = reactive({
  customerEmail: userStore.customerEmail ?? null,
  emailConfirmStatus: 0,
  checkCodeDigits: [null, null, null, null, null, null],
  showConflictMessage: false,
  seconds: 30,
});

watch(
  () => emailConfirm.checkCodeDigits,
  (value) => {
    const otp = value.find((d) => /^\d{2,6}$/gi.test(d));
    if (otp) {
      emailConfirm.checkCodeDigits = otp.toString().padStart(6, "0").split("");
      submitCheckCode();
    }
  },
  { deep: true }
);

function toStartStatus() {
  emailConfirm.emailConfirmStatus = 0;
}

async function submitEmail() {
  nonce.value = generateNonce();
  let emailChangeRequest = {
    nonce: nonce.value,
    email: emailConfirm.customerEmail,
    bnpl: {
      publicId: route.params.publicId,
      financier: route.params.financier,
    },
  };
  if (props.inCustomerLimit) {
    emailChangeRequest.customerLimitId = customerLimitStore.limit.id;
  }
  try {
    let result = await profileStore.emailChange(emailChangeRequest);
    secondFactorId = result.id;
    createCountDown();
    emailConfirm.emailConfirmStatus = 1;
    emailConfirm.showConflictMessage = false;
  } catch (e) {
    if (e.status === 409) {
      emailConfirm.showConflictMessage = true;
    }
  }
}

async function submitCheckCode() {
  if (isCheckCodeSubmitted.value) {
    return;
  }

  let fullCheckCode = emailConfirm.checkCodeDigits.join("");

  let emailConfirmRequest = {
    nonce: nonce.value,
    code: fullCheckCode,
    id: secondFactorId,
  };

  isCheckCodeSubmitted.value = true;

  try {
    if (route.params.publicId) {
      emailConfirmRequest.bnplPublicId = route.params.publicId;
    }
    await profileStore.confirmEmail(emailConfirmRequest);
    emailConfirm.seconds = 0;
    await profileStore.fetch();
    redirectToConfirmed();
  } catch (e) {
    emailConfirm.checkCodeDigits = [null, null, null, null, null, null];
    emailConfirm.emailConfirmStatus = 2;
    isCheckCodeSubmitted.value = false;
  }
}

let pollInterval = null;

function startPolling() {
  stopPolling();
  pollInterval = setInterval(async () => {
    try {
      await profileStore.fetch();
      if (profileStore.profile && profileStore.profile.emailConfirmed) {
        redirectToConfirmed();
      }
    } catch (e) {
      console.log(e);
    }
  }, 3000);
}

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

// The magic link can be opened on a different tab/device, so the screen showing the code input
// has to notice that on its own instead of waiting for the customer to come back and click continue.
function redirectToConfirmed() {
  stopPolling();
  if (route.name === "financier-publicId") {
    navigateTo({
      name: "financier-email-confirmed",
      query: { publicId: route.params.publicId },
    });
  } else if (route.name === "financier-prescore-prescoreId") {
    navigateTo({
      name: "financier-email-confirmed",
      query: { publicId: route.params.prescoreId, prescore: "true" },
    });
  } else if (props.inCustomerLimit) {
    navigateTo({ name: "financier-customer-limit-email-confirmed" });
  } else {
    emailConfirm.emailConfirmStatus = 3;
  }
}

watch(
  () => emailConfirm.emailConfirmStatus,
  (status) => {
    if (status === 1) {
      startPolling();
    } else {
      stopPolling();
    }
  }
);

function focusFromTo(event, digitIndex, toRef, prevRef) {
  if (event.key === "ArrowLeft") {
    prevRef.value.focus();
  } else if (event.key === "ArrowRight") {
    toRef.value.focus();
  } else if (event.key === "Backspace") {
    event.preventDefault();
    emailConfirm.checkCodeDigits[digitIndex] = "";
    prevRef.value.focus();
  } else if (/^\d$/gi.test(event.key)) {
    event.preventDefault();
    emailConfirm.checkCodeDigits[digitIndex] = event.key;
    if (digitIndex === 5) {
      submitCheckCodeButton.value.focus();
    } else {
      toRef.value.focus();
    }
  }
}

async function resendCheckCode() {
  emailConfirm.seconds = 30;
  createCountDown();
  nonce.value = generateNonce();
  let emailChangeRequest = {
    nonce: nonce.value,
    email: emailConfirm.customerEmail,
    bnpl: {
      publicId: route.params.publicId,
      financier: route.params.financier,
    },
  };
  if (props.inCustomerLimit) {
    emailChangeRequest.customerLimitId = customerLimitStore.limit.id;
  }
  let result = await profileStore.sendConfirmationEmail(emailChangeRequest);
  secondFactorId = result.id;
}

function stepToFlow() {
  bnplApplicationStore.nextStep();
}

function nextLimitStep() {
  limitStore.nextPosition();
}

function nextCustomerLimitStep() {
  customerLimitStore.nextStep(countryStore.countryId);
}

function previousLimitStep() {
  limitStore.previousPosition();
}

function createCountDown() {
  let countDown = setInterval(() => {
    emailConfirm.seconds--;
    if (emailConfirm.seconds === 0) {
      clearInterval(countDown);
    }
  }, 1000);
}
</script>

<style scoped>
.checkCodeInputField {
  width: 48px;
  height: 48px;
  text-align: center;
  flex: 0 1 48px;
  min-width: 0;
  aspect-ratio: 1;
}

.justify-content-space-between {
  justify-content: space-between;
}

.checkCodeInputField:disabled {
  color: var(--gray200);
  border-color: var(--gray200);
}

.checkCodeDigitsContainer {
  gap: 16px;
  width: 100%;
  justify-content: center;
}
.tryAgain {
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 28px;
  font-feature-settings: "liga" off;
  color: var(--systemError);
}
.successful {
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 28px;
  font-feature-settings: "liga" off;
  color: var(--systemSuccess);
}

.buttonContainer {
  display: grid;
  grid-gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: end;
}

@media only screen and (max-width: 792px) {
  .buttonContainer {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .checkCodeDigitsContainer {
    gap: 8px;
  }
}
</style>
